/**
 * Phase1TaskRow.tsx
 * FEAT-2026-0007 Phase 6B.4 — Single row in the Phase-1 scenario task table.
 *
 * Columns rendered: # · Task Name · Test Qty · Machine Qty (edit) ·
 *   Assigned Machine(s) (capable-filtered) · Start Date · End Date ·
 *   Duration (Days, computed) · Actions
 *
 * No Assignees column — that is Phase 2 (6C.1).
 *
 * Phase 7.5 — Prep-row indicator + ConfirmDialog for removal.
 *   Prep rows (IsPrepStep=1) render a "Prep" Chip in the Task Name cell so lab
 *   staff know the row was auto-inserted.  Clicking the remove button on a prep
 *   row shows a ConfirmDialog explaining that the removal sticks: the prep step
 *   will NOT reappear on the next generate-tasks recompute (7.3's prepTaskState
 *   honours soft-deleted prep rows — "deleted → skip").  The only way a removed
 *   prep comes back is an explicit admin reset (out of scope per the plan).
 *   The ConfirmDialog is NOT shown for regular (non-prep) tasks — those still
 *   delete immediately without a confirmation modal, matching the prior UX.
 *   Removing a prep row soft-deletes only the per-schedule ScheduleTask instance;
 *   the RockPrepStep config row is never touched.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircleOutline, Delete, ErrorOutline } from '@mui/icons-material';
import BuildIcon from '@mui/icons-material/Build';

import { MachineSelectCell } from '../../tasks/MachineSelectCell';
import {
  ConfirmDialog,
  CONFIRM_INITIAL,
  type ConfirmDialogState,
} from '../../../../components/shared/ConfirmDialog';
import { PlacementConfirmDialog } from '../../../../components/schedule/PlacementConfirmDialog';
import { MachineQtyEditor } from '../../../../components/schedule/MachineQtyEditor';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';
import { computeTaskDurationDays, taskDurationDisplay } from './taskDuration';
import { formatMachineTime } from './taskRowMath';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Phase1TaskRowProps {
  task: TaskWithBookings;
  rowNumber: number | string;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: (key: string) => void;
  onUpdateMachineCount: (taskKey: string, count: number) => Promise<void>;
  onChangeMachine: (bookingKey: string, machineKey: string) => Promise<void>;
  onChangeBookingStart: (
    bookingKey: string,
    startDate: string,
    opts?: { force?: boolean },
  ) => Promise<
    | { kind: 'updated' }
    | { kind: 'needsConfirm'; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null }
  >;
  onPlaceFirstMachine: (taskKey: string, machineKey: string) => Promise<void>;
  onDelete: (taskKey: string) => Promise<void>;
  /**
   * Item-4 UX: clicking the task name flashes the matching bar in the bottom
   * Machine Schedule grid so the planner can spot which row it is.
   */
  onActivate?: () => void;
  /**
   * FEAT-2026-0007 conflict-tag fix: suggested earliest-free start when THIS row's
   * booking is part of a hard conflict (derived from the loaded conflicts). Drives
   * the red outline + "Use <date>" hint at load — a fresh local edit still wins.
   */
  conflictSuggestedStart?: string | null;
  /** Stat-holiday ISO dates in the window — so Duration excludes them (working days). */
  holidays?: readonly string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format an ISO date string to a short human-readable form: 'May 22, 2026' */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

/**
 * Collapse a redundant generated task name for display. Names are built as
 * `${lineDescription} — ${taskTypeName}` at generation, so when the test name
 * equals its task-type name you get "X — X", and the row also shows the type as
 * a subtitle → it reads as a triplet. Since the subtitle already carries the
 * type, drop a trailing " — {type}" (or collapse an "A — A") so the bold line is
 * just the distinctive part. Works on existing rows too (no re-generation needed).
 */
function displayTaskName(name: string, typeName: string | null): string {
  if (!name) return name;
  if (typeName) {
    if (name === typeName) return name;
    const suffix = ` — ${typeName}`;
    if (name.endsWith(suffix)) {
      const head = name.slice(0, -suffix.length).trim();
      if (head) return head;
    }
  }
  const parts = name.split(' — ');
  if (parts.length === 2 && parts[0].trim() === parts[1].trim()) return parts[0].trim();
  return name;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase1TaskRow: React.FC<Phase1TaskRowProps> = ({
  task,
  rowNumber,
  bulkMode,
  selected,
  onToggleSelect,
  onUpdateMachineCount,
  onChangeMachine,
  onChangeBookingStart,
  onPlaceFirstMachine,
  onDelete,
  onActivate,
  conflictSuggestedStart = null,
  holidays = [],
}) => {
  const theme = useTheme();

  const [startInput, setStartInput] = useState(task.StartDate ?? '');
  const [startSaving, setStartSaving] = useState(false);

  // The current per-scenario machine count = the live booking count (placed machines).
  // For an unplaced task (no bookings) fall back to the persisted intended count so the
  // planner can still set how many machines to split across before placing.
  const liveCount = task.Bookings.length > 0 ? task.Bookings.length : task.MachineCount;
  // Fresh-edit warn-and-confirm: a chosen Start that overlaps or lands on a
  // weekend/holiday holds here until the planner confirms or picks the earliest
  // free slot (mirrors the grid drag's PlacementConfirmDialog).
  const [pendingStart, setPendingStart] = useState<
    { picked: string; conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null } | null
  >(null);
  const [deleting, setDeleting] = useState(false);
  // Phase 7.5 — prep-removal confirmation dialog state.
  const [confirmState, setConfirmState] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Keep start input in sync when the parent refreshes the task prop
  useEffect(() => {
    setStartInput(task.StartDate ?? '');
    setPendingStart(null);
  }, [task.StartDate]);


  const commitStart = async (picked: string, force = false) => {
    if (!picked || (!force && picked === task.StartDate) || task.Bookings.length === 0) return;
    setStartSaving(true);
    try {
      // Apply to every booking on the task. The first soft barrier (overlap or
      // weekend/holiday) opens the confirm dialog; `force` skips the check after
      // the planner confirms "book anyway".
      let barrier:
        | { conflict: boolean; weekend: boolean; earliestFree: string; conflictLabel?: string | null }
        | null = null;
      for (const b of task.Bookings) {
        const result = await onChangeBookingStart(b.MachineBookingKey, picked, { force });
        if (result.kind === 'needsConfirm' && !barrier) {
          barrier = {
            conflict: result.conflict,
            weekend: result.weekend,
            earliestFree: result.earliestFree,
            conflictLabel: result.conflictLabel ?? null,
          };
        }
      }
      if (barrier && !force) {
        // Hold: keep the input showing the attempted date and open the dialog.
        setPendingStart({ picked, ...barrier });
      }
    } finally {
      setStartSaving(false);
    }
  };

  /**
   * Phase 7.5 — execute the soft-delete (shared by both prep and regular rows).
   * For prep rows this is the action that fires after the user confirms; for
   * regular rows it fires immediately when they click the remove button.
   */
  const executeDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(task.ScheduleTaskKey);
    } catch {
      setDeleting(false);
    }
  };

  /**
   * Phase 7.5 — prep rows require a confirmation before deletion because the
   * removal "sticks" across recomputes (intentional, REQ-06 "users can remove
   * them").  Regular rows delete immediately without a modal.
   */
  const handleDelete = () => {
    if (task.IsPrepStep) {
      // Show confirmation explaining the "stays gone" semantics.
      setConfirmState({
        open: true,
        title: 'Remove prep step?',
        message:
          'This is an auto-inserted prep step (Cutting / Grinding / Conformity). ' +
          'Removing it will NOT re-insert it on the next schedule recompute — ' +
          'only a schedule reset can bring it back. ' +
          'The prep-step configuration (in Settings → Rock Prep Steps) is not affected.',
        confirmLabel: 'Remove',
        confirmColor: 'warning',
        onConfirm: () => { void executeDelete(); },
      });
    } else {
      void executeDelete();
    }
  };

  const statusColor =
    task.Bookings.some((b) => b.Status === 'Committed')
      ? theme.schedule.committed
      : theme.schedule.tentative;

  // Run Mode Phase C: continuous tasks count CALENDAR days ('N cal days') and
  // label machine time as elapsed 24/7 run-time; attended rows are unchanged.
  const duration = computeTaskDurationDays(task.StartDate, task.EndDate, holidays, task.RunMode);
  const durationDisplay = taskDurationDisplay(duration, task.RunMode);
  const machineTime = formatMachineTime(task.MachineMinutes, task.TestQty, task.IsPrepStep, task.RunMode);
  // De-duplicated name for display (collapses the "X — X" triplet). The subtitle
  // below shows the task-type name only when it adds something the bold line doesn't.
  const displayName = displayTaskName(task.Name, task.TaskTypeName);
  const showTypeSubtitle = Boolean(task.TaskTypeName) && task.TaskTypeName !== displayName;

  // Red outline + "Use <date>" hint for a booking that is ALREADY part of a
  // loaded hard conflict. A fresh edit instead routes through the
  // PlacementConfirmDialog below (pendingStart), so no startConflict state here.
  const effectiveConflict = conflictSuggestedStart;

  return (
    <TableRow
      hover
      selected={selected}
      onClick={onActivate}
      sx={{
        opacity: deleting ? 0.4 : 1,
        transition: 'opacity 0.2s ease',
        cursor: onActivate ? 'pointer' : 'inherit',
        '& td': { py: 1, px: 1.5 },
      }}
    >
      {/* Bulk-select checkbox */}
      {bulkMode && (
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelect(task.ScheduleTaskKey)}
          />
        </TableCell>
      )}

      {/* # row number */}
      <TableCell align="center">
        <Typography variant="caption" color="text.secondary">
          {rowNumber}
        </Typography>
      </TableCell>

      {/* Task Name + type hint + Phase 7.5 "Prep" badge */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem' }}>
            {displayName}
          </Typography>
          {/* Phase 7.5: Prep indicator chip — only on auto-inserted prep rows (IsPrepStep=1).
              Uses theme.schedule.machineTime token (slate-blue) to visually associate prep
              with machine-time, and BuildIcon to signal "lab process step". */}
          {task.IsPrepStep && (
            <Tooltip title="Auto-inserted prep step ($0, hidden in quote). Removal sticks across recomputes.">
              <Chip
                icon={<BuildIcon sx={{ fontSize: '0.7rem !important' }} />}
                label="Prep"
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: 18,
                  backgroundColor: alpha(theme.schedule.machineTime, 0.12),
                  color: theme.schedule.machineTime,
                  '& .MuiChip-icon': { color: theme.schedule.machineTime },
                  cursor: 'default',
                }}
              />
            </Tooltip>
          )}
        </Box>
        {showTypeSubtitle && (
          <Typography variant="caption" color="text.secondary">
            {task.TaskTypeName}
          </Typography>
        )}
      </TableCell>

      {/* Test Qty (read-only — from quote/WO line item) */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
          {task.TestQty != null ? task.TestQty : '—'}
        </Typography>
      </TableCell>

      {/* Machine Qty (editable, explicit apply) */}
      <TableCell align="center">
        <MachineQtyEditor
          liveCount={liveCount}
          onApply={(c) => onUpdateMachineCount(task.ScheduleTaskKey, c)}
        />
      </TableCell>

      {/* Assigned Machine(s) — capable-filtered dropdown */}
      <TableCell>
        {task.Bookings.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ErrorOutline sx={{ fontSize: 16, color: theme.schedule.conflictSoft }} />
              <Typography variant="caption" color="text.secondary">
                Pick a machine:
              </Typography>
            </Box>
            <MachineSelectCell
              taskTypeKey={task.TaskTypeKey}
              taskTypeName={task.TaskTypeName}
              currentMachineKey=""
              bookingKey="__initial__"
              showNoCapableWarning
              onChangeMachine={async (_bookingKey, machineKey) => {
                await onPlaceFirstMachine(task.ScheduleTaskKey, machineKey);
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {task.Bookings.map((booking, bIdx) => (
              <Box
                key={booking.MachineBookingKey}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                {task.Bookings.length > 1 && (
                  <Typography variant="caption" sx={{ minWidth: 16, color: 'text.secondary' }}>
                    {bIdx + 1}.
                  </Typography>
                )}
                <MachineSelectCell
                  taskTypeKey={task.TaskTypeKey}
                  currentMachineKey={booking.MachineKey}
                  bookingKey={booking.MachineBookingKey}
                  onChangeMachine={onChangeMachine}
                />
                <Chip
                  label={booking.Status}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    backgroundColor: alpha(statusColor, 0.12),
                    color: statusColor,
                    flexShrink: 0,
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </TableCell>

      {/* Start Date (editable) */}
      <TableCell align="center">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              type="date"
              value={startInput}
              onChange={(e) => {
                // BUG-2026-0106: commit (and recompute conflicts/compatibility) as
                // soon as a date is picked, not only on blur. A native
                // <input type="date"> fires `change` once with a complete, valid
                // ISO date (or '' when cleared), so committing here is safe —
                // commitStart already no-ops on empty/unchanged/no-booking input.
                const next = e.target.value;
                setStartInput(next);
                if (next) void commitStart(next);
              }}
              // Skip the blur-commit while a confirm is pending — otherwise it
              // re-fires the same date (task.StartDate is still the old value)
              // and re-opens the dialog (double request + flicker).
              onBlur={() => { if (!pendingStart) void commitStart(startInput); }}
              // RISK-03: do NOT disable while saving — disabling tears down the
              // open native <input type=date> calendar popup mid-selection, so
              // clicks on another month never register. The spinner still shows.
              disabled={task.Bookings.length === 0}
              inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px', width: 110 } }}
              sx={{
                width: 130,
                '& .MuiInputBase-input': { textAlign: 'center' },
                ...(effectiveConflict
                  ? {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.schedule.conflictHard,
                    },
                  }
                  : {}),
              }}
            />
            {startSaving && <CircularProgress size={14} />}
          </Box>
          {effectiveConflict && (
            <Tooltip title={`Use ${effectiveConflict} — the earliest free slot.`}>
              <Typography
                onClick={() => { void commitStart(effectiveConflict); }}
                variant="caption"
                sx={{
                  color: theme.schedule.conflictHard,
                  fontSize: '0.65rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Use {fmtDate(effectiveConflict)}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </TableCell>

      {/* End Date (read-only) */}
      <TableCell align="center">
        <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          {fmtDate(task.EndDate)}
        </Typography>
      </TableCell>

      {/* Duration (computed from Start and End; per-mode unit — Phase C) */}
      <TableCell align="center">
        <Tooltip title={durationDisplay.tooltip}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
            {durationDisplay.text}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Machine Time — "show both inline" (Gigi 2026-06-19): rate × qty = total.
          Test rows store the per-test rate; prep rows store the qty-scaled total.
          formatMachineTime normalises both to "45 × 25 = 1,125 min". */}
      <TableCell align="center">
        {machineTime ? (
          <Tooltip title={machineTime.tooltip}>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.78rem', color: theme.schedule.machineTime }}
            >
              {machineTime.text}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>
            —
          </Typography>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          {task.Bookings.length > 0 && (
            <Tooltip title="Machine(s) placed">
              <CheckCircleOutline sx={{ fontSize: 16, color: theme.schedule.committed }} />
            </Tooltip>
          )}
          {/* Phase 7.5: prep rows show "Remove prep step" tooltip; regular rows keep "Remove task". */}
          <Tooltip title={task.IsPrepStep ? 'Remove prep step (sticks across recomputes)' : 'Remove task'}>
            <span>
              <IconButton
                size="small"
                onClick={handleDelete}
                disabled={deleting}
                sx={{
                  color: theme.schedule.conflictHard,
                  '&:hover': { backgroundColor: alpha(theme.schedule.conflictHard, 0.08) },
                }}
              >
                {deleting ? <CircularProgress size={14} /> : <Delete fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Phase 7.5 — prep-removal confirmation dialog.
          Only rendered for prep rows; never shown for regular tasks. */}
      <ConfirmDialog
        state={confirmState}
        onClose={() => setConfirmState(CONFIRM_INITIAL)}
      />

      {/* Warn-and-confirm gate for a fresh Start edit that overlaps another
          booking and/or lands on a weekend/holiday (drag-parity). */}
      <PlacementConfirmDialog
        open={pendingStart !== null}
        conflict={pendingStart?.conflict ?? false}
        weekend={pendingStart?.weekend ?? false}
        conflictLabel={pendingStart?.conflictLabel ?? null}
        earliestFree={pendingStart?.earliestFree ?? null}
        detail={displayName}
        saving={startSaving}
        onConfirm={() => {
          const p = pendingStart;
          if (p) void commitStart(p.picked, true).finally(() => setPendingStart(null));
        }}
        onUseEarliest={() => {
          const p = pendingStart;
          if (p) void commitStart(p.earliestFree, false).finally(() => setPendingStart(null));
        }}
        onCancel={() => {
          setPendingStart(null);
          setStartInput(task.StartDate ?? '');
        }}
      />
    </TableRow>
  );
};
